import laggy from 'api/middleware/laggy';
import { getSmartCollection } from 'api/product/getSmartCollection';
import { SmartCollectionResponse } from 'api/product/getSmartCollection/types';
import { BasicPDEXInterface } from 'api/product/types';
import { CollectionPropTypes } from 'modules/collection/types/CollectionPropTypes';
import { useRegion } from 'hooks/use-region';
import { useSavingPlanStore } from 'stores/saving-plan';
import useSWR, { SWRConfiguration, SWRResponse } from 'swr';

interface InputBaseType extends BasicPDEXInterface {
  collectionDefault?: SmartCollectionResponse;
  swrOptions?: SWRConfiguration;
  couponCode?: string;
}

type Input = CollectionPropTypes<InputBaseType> & {
  skip?: boolean;
};

type SWRKey = [
  Input['collectionHandle'],
  Input['segmentation'],
  Input['smartCollectionInput'],
  Input['ninetailed'],
  Input['audiences'],
  Input['couponCode'],
  string | undefined,
];

const useCollectionFetch = ({
  skip,
  collectionDefault,
  swrOptions,
  collectionHandle,
  segmentation,
  smartCollectionInput,
  ninetailed,
  audiences,
  couponCode,
}: Input): {
  data: SmartCollectionResponse | undefined;
  isLoading: boolean;
} => {
  const region = useRegion();
  const activePlan = useSavingPlanStore((state) => state.activePlan);
  const hasDislikedProteinInput = smartCollectionInput?.disliked_protein_tags !== undefined;

  /**
   * Refetch Data on client side so we always get updated data
   */
  const { data, isLoading, isValidating, isLagging } = useSWR(
    skip
      ? null
      : [
          collectionHandle,
          segmentation,
          smartCollectionInput,
          ninetailed,
          audiences,
          couponCode,
          region,
        ],
    async ([
      collectionHandle,
      segmentation,
      smartCollectionInput,
      ninetailed,
      audiences,
      couponCode,
    ]: SWRKey) => {
      if (!collectionHandle && !segmentation && !smartCollectionInput) {
        throw new Error('no handle and segmentation is assigned');
      }

      // Remove EC audience from smartCollectionInput
      // so that we can apply control experience for EC customer experience
      if (smartCollectionInput) {
        return getSmartCollection(
          {
            ...smartCollectionInput,
            coupon_code: couponCode,
            ninetailed,
            audiences,
          },
          activePlan
        );
      }

      if (collectionHandle) {
        return getSmartCollection({ slug: collectionHandle }, activePlan);
      }

      throw new Error('no handle and segmentation is assigned');
    },
    {
      fallback: {
        ...(collectionHandle ? { [collectionHandle]: collectionDefault } : {}),
      },
      use: [laggy],
      ...swrOptions,
    }
  ) as SWRResponse<SmartCollectionResponse> & { isLagging: boolean };

  const currentData = hasDislikedProteinInput && isLagging ? undefined : data;

  return {
    data: currentData ?? (hasDislikedProteinInput ? undefined : collectionDefault),
    isLoading: isLoading || isValidating,
  };
};

export default useCollectionFetch;
